import {
  fmtCurrencyAmount,
  fmtNumberAbbr,
} from '~/utils'
import {
  getLotSizeFromMeta,
  getPricePerAcreFromTotalPrice,
  getPricePerSfFromTotalPrice,
  getTotalPriceFromAcrePrice,
  getTotalPriceFromSfPrice,
  type PriceRange,
} from './lotSize'
import type { ListingMeta } from './types'

type LandLotSizeBand = 'small' | 'medium' | 'large'

export type LandDetailPriceValue = {
  key: string
  label: string
  value: string
}

const PRICE_PER_SF_MIN = 0.25
const PRICE_PER_SF_MAX = 100
const PRICE_PER_ACRE_MIN = 10_000
const PRICE_PER_ACRE_MAX = 5_000_000

type LandDetailPriceFormat = 'abbr' | 'full'

function hasPriceRangeValue(range: PriceRange | undefined): range is PriceRange {
  return range?.min !== undefined || range?.max !== undefined
}

function getRangeValue(range: PriceRange, key: 'min' | 'max') {
  return range[key] ?? range.min ?? range.max
}

function isRangeWithin(
  range: PriceRange | undefined,
  minThreshold: number,
  maxThreshold: number
) {
  if (!hasPriceRangeValue(range)) return false

  const min = getRangeValue(range, 'min')
  const max = getRangeValue(range, 'max')

  return (
    min !== undefined &&
    max !== undefined &&
    min >= minThreshold &&
    max <= maxThreshold
  )
}

function getLotSizeBand(acres: number): LandLotSizeBand {
  if (acres < 2) return 'small'
  if (acres <= 20) return 'medium'
  return 'large'
}

function formatRange(
  range: PriceRange,
  formatter: (value: number) => string
) {
  const min = getRangeValue(range, 'min')
  const max = getRangeValue(range, 'max')

  if (min === undefined && max === undefined) return ''
  if (min !== undefined && max !== undefined && min !== max) {
    return `${formatter(min)} - ${formatter(max)}`
  }

  return formatter((min ?? max)!)
}

function formatTotalPrice(range: PriceRange) {
  return formatRange(range, (value) => `$${fmtCurrencyAmount(value)}`)
}

function formatPricePerSf(range: PriceRange) {
  return formatRange(range, (value) => `$${fmtCurrencyAmount(value)}/SF`)
}

function formatPricePerAcre(
  range: PriceRange,
  format: LandDetailPriceFormat
) {
  return formatRange(range, (value) =>
    format === 'abbr'
      ? `$${fmtNumberAbbr(value)}/ac`
      : `$${fmtCurrencyAmount(value)}/ac`
  )
}

function getPrimaryLabel(priceType: ListingMeta['priceType'], status: string) {
  if (priceType === 'sf') return 'Price Per SF'
  if (priceType === 'acre') return 'Price Per Acre'

  return status !== 'active' && status !== 'pending'
    ? 'Listed Price'
    : 'Sale Price'
}

export function getLandDetailPriceValues(
  meta: ListingMeta,
  status: string,
  {
    pricePerAcreFormat = 'abbr',
  }: {
    pricePerAcreFormat?: LandDetailPriceFormat
  } = {}
): LandDetailPriceValue[] {
  if (!meta.price || meta.priceNegotiable) {
    return []
  }

  const priceType = meta.priceType || 'total'
  const enteredPrice = {
    min: meta.price,
    max: meta.priceMax || meta.price,
  }
  const lotSize = getLotSizeFromMeta(meta)
  const hasLotSize = !!lotSize.acres.min && !!lotSize.sf.min
  const values: LandDetailPriceValue[] = [
    {
      key: 'price',
      label: getPrimaryLabel(priceType, status),
      value:
        priceType === 'sf'
          ? formatPricePerSf(enteredPrice)
          : priceType === 'acre'
            ? formatPricePerAcre(enteredPrice, pricePerAcreFormat)
            : formatTotalPrice(enteredPrice),
    },
  ]

  if (!hasLotSize) {
    return values
  }

  let totalPrice: PriceRange | undefined
  let pricePerSf: PriceRange | undefined
  let pricePerAcre: PriceRange | undefined

  if (priceType === 'sf') {
    pricePerSf = enteredPrice
    totalPrice = getTotalPriceFromSfPrice(pricePerSf, lotSize.sf)
    if (lotSize.unit !== 'sf') {
      pricePerAcre = getPricePerAcreFromTotalPrice(totalPrice, lotSize.acres)
    }
  } else if (priceType === 'acre') {
    pricePerAcre = enteredPrice
    totalPrice = getTotalPriceFromAcrePrice(pricePerAcre, lotSize.acres)
    pricePerSf = getPricePerSfFromTotalPrice(totalPrice, lotSize.sf)
  } else {
    totalPrice = enteredPrice
    pricePerSf = getPricePerSfFromTotalPrice(totalPrice, lotSize.sf)
    if (lotSize.unit !== 'sf') {
      pricePerAcre = getPricePerAcreFromTotalPrice(totalPrice, lotSize.acres)
    }
  }

  const acresMin = lotSize.acres.min
  if (acresMin === undefined) {
    return values
  }

  const lotSizeBand = getLotSizeBand(acresMin)
  const canShowPricePerSf =
    isRangeWithin(pricePerSf, PRICE_PER_SF_MIN, PRICE_PER_SF_MAX) &&
    lotSizeBand !== 'large'
  const canShowPricePerAcre =
    isRangeWithin(pricePerAcre, PRICE_PER_ACRE_MIN, PRICE_PER_ACRE_MAX) &&
    lotSizeBand !== 'small'

  if (priceType !== 'sf' && canShowPricePerSf && pricePerSf) {
    values.push({
      key: 'pricePerSf',
      label: 'Price Per SF',
      value: formatPricePerSf(pricePerSf),
    })
  }

  if (priceType !== 'acre' && canShowPricePerAcre && pricePerAcre) {
    values.push({
      key: 'pricePerAcre',
      label: 'Price Per Acre',
      value: formatPricePerAcre(pricePerAcre, pricePerAcreFormat),
    })
  }

  return values
}
